import React, { useMemo } from 'react';
import type { AssignmentCoverImage, ProjectAssignmentItem } from '../../types/assignmentPointsNote';
import { summarizeProjectSubTasks } from '../../utils/assignmentPointsNoteProgress';
import { challengeThemeStyle } from '../../types/assignmentChallenge';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';
import { ChallengeThumb } from '../assignmentChallenge/ChallengeStage';
import type ProjectAssignmentDetailSection from './ProjectAssignmentDetailSection';

type ChallengeTools = NonNullable<React.ComponentProps<typeof ProjectAssignmentDetailSection>['challenge']>;

type Props = {
  item: ProjectAssignmentItem;
  coverImage?: AssignmentCoverImage | null;
  isShared?: boolean;
  /** 설정·복제·삭제 버튼 묶음 */
  actions: React.ReactNode;
  /** 날짜·포인트·보상 메타 */
  meta: React.ReactNode;
  memberIdSet?: Set<string> | null;
  activeSubTaskId?: string | null;
  rewardProcessing?: boolean;
  /** 없으면 일반 프로젝트 과제(하위과제 카드 · 편집/QR 버튼 없음) */
  challenge?: ChallengeTools | null;
  onOpenMonitoring: () => void;
  onGrantReward?: () => void;
  onSelectSubTask: (subTaskId: string, index: number) => void;
};

const ChallengeProjectDashboard: React.FC<Props> = ({
  item,
  coverImage,
  isShared,
  actions,
  meta,
  memberIdSet,
  activeSubTaskId,
  rewardProcessing = false,
  challenge,
  onOpenMonitoring,
  onGrantReward,
  onSelectSubTask,
}) => {
  const summaries = useMemo(() => summarizeProjectSubTasks(item, memberIdSet), [item, memberIdSet]);
  const submittedBySubTask = challenge?.submittedCountBySubTask;
  const isChallenge = !!challenge;

  const totals = useMemo(() => {
    let slots = 0;
    let submitted = 0;
    summaries.forEach((s) => {
      slots += s.totalStudents;
      submitted += submittedBySubTask
        ? Math.min(s.totalStudents, submittedBySubTask[s.subTaskId] ?? 0)
        : s.totalStudents - s.pendingStudents;
    });
    return { slots, submitted };
  }, [summaries, submittedBySubTask]);

  return (
    <div
      className="cp-apn-block-wrap cp-chl-dash"
      style={challengeThemeStyle(item.color)}
      data-tutorial-apn="project-detail"
    >
      <header className={`cp-chl-dash-hero${coverImage ? ' has-cover' : ''}`}>
        {coverImage ? (
          <div className="cp-chl-dash-hero-cover" aria-hidden>
            <CachedAssignmentCoverImage image={coverImage} className="cp-chl-dash-hero-cover-img" fallback={null} />
          </div>
        ) : null}
        <div className="cp-chl-dash-hero-top">
          <span className="cp-chl-dash-badge">{isChallenge ? '챌린지' : '프로젝트'}</span>
          {isShared ? <span className="cp-chl-dash-badge is-ghost">공유과제</span> : null}
          <span className="cp-chl-dash-hero-tools">{actions}</span>
        </div>
        <h3 className="cp-chl-dash-title">{item.title || (isChallenge ? '챌린지 프로젝트' : '프로젝트 과제')}</h3>
        <div className="cp-chl-dash-meta">{meta}</div>
        {totals.slots > 0 ? (
          <span className="cp-chl-dash-hero-stat">
            {isChallenge ? '제출' : '완료'} {totals.submitted}/{totals.slots}
          </span>
        ) : null}
      </header>

      <div className={`cp-chl-dash-btns${isChallenge ? '' : ' is-project'}`}>
        {challenge ? (
          <>
            <button type="button" className="cp-chl-dash-btn is-primary" onClick={challenge.onEdit}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                <path d="M4 20h4L19 9l-4-4L4 16v4z" fill="currentColor" />
              </svg>
              인증과제 편집
            </button>
            <button type="button" className="cp-chl-dash-btn" onClick={challenge.onShare}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
                <path
                  d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm8-2h3v3h-3v-3zm5 0h3v3h-3v-3zm-5 5h3v3h-3v-3zm5 0h3v3h-3v-3z"
                  fill="currentColor"
                />
              </svg>
              참여 링크 · QR
            </button>
          </>
        ) : null}
        <button
          type="button"
          className={`cp-chl-dash-btn${isChallenge ? '' : ' is-primary'}`}
          data-tutorial-apn="project-reach"
          onClick={onOpenMonitoring}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
            <rect x="3" y="12" width="4" height="9" rx="1.2" fill="currentColor" />
            <rect x="10" y="7" width="4" height="14" rx="1.2" fill="currentColor" />
            <rect x="17" y="3" width="4" height="18" rx="1.2" fill="currentColor" />
          </svg>
          멤버별 도달률
        </button>
        {item.rewardEnabled ? (
          <button
            type="button"
            className="cp-chl-dash-btn"
            data-tutorial-apn="project-reward"
            disabled={rewardProcessing}
            onClick={onGrantReward}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden>
              <path d="M3 10h18v11H3V10zm-1-4h20v4H2V6z" fill="currentColor" />
            </svg>
            {rewardProcessing ? '보상 처리 중...' : '보상 지급'}
          </button>
        ) : null}
      </div>

      <div className="cp-chl-dash-list" data-tutorial-apn="project-subtasks">
        {summaries.length === 0 ? (
          <p className="cp-chl-dash-empty">
            {isChallenge
              ? '아직 인증과제가 없어요. 인증과제 편집에서 만들어 주세요.'
              : '아직 하위과제가 없어요. 과제 설정에서 만들어 주세요.'}
          </p>
        ) : null}
        {summaries.map((s) => {
          const complete = s.pendingStudents === 0 && s.totalStudents > 0;
          const active = activeSubTaskId === s.subTaskId;
          const index = item.subTasks.findIndex((t) => t.id === s.subTaskId);
          const info = challenge?.cardInfoBySubTask?.[s.subTaskId];
          const hidden = info ? !info.isVisible : false;
          const submitted = submittedBySubTask?.[s.subTaskId] ?? 0;
          const unit = isChallenge ? '인증과제' : '하위과제';
          const done = s.totalStudents - s.pendingStudents;
          const pct = s.totalStudents > 0 ? Math.round((done / s.totalStudents) * 100) : 0;
          return (
            <button
              key={s.subTaskId}
              type="button"
              className={`cp-chl-dash-card${complete ? ' is-complete' : ''}${active ? ' is-active' : ''}${
                hidden ? ' is-hidden' : ''
              }`}
              onClick={() => onSelectSubTask(s.subTaskId, Math.max(0, index))}
              aria-label={`${s.order}번 ${unit} ${s.title || ''} 체크하기`}
            >
              <span className="cp-chl-dash-card-img">
                {isChallenge ? (
                  <ChallengeThumb url={info?.thumbnailUrl ?? null} title={s.title} />
                ) : (
                  <span className="cp-chl-dash-card-num" aria-hidden>
                    <small>{unit}</small>
                    {s.order}
                  </span>
                )}
              </span>
              <span className="cp-chl-dash-card-body">
                <span className="cp-chl-dash-card-tags">
                  <span className={`cp-chl-dash-card-badge${complete ? ' is-done' : ''}`}>
                    {complete ? '모두 확인' : `${unit} ${s.order}`}
                  </span>
                  {hidden ? <span className="cp-chl-dash-card-badge is-muted">숨김</span> : null}
                </span>
                <span className="cp-chl-dash-card-title">{s.title || unit}</span>
                <span className="cp-chl-dash-card-meta">
                  {s.points}pt
                  {info?.missionCount ? ` · 인증 항목 ${info.missionCount}개` : ''}
                </span>
                <span className="cp-chl-dash-card-stats">
                  <span>
                    <i className="is-sub" aria-hidden />
                    {isChallenge ? `제출 ${submitted}` : `완료 ${done}`}
                  </span>
                  <span>
                    <i className="is-pending" aria-hidden />
                    미확인 {s.pendingStudents}
                  </span>
                </span>
                <span className="cp-chl-dash-card-progress" aria-hidden>
                  <span style={{ width: `${pct}%` }} />
                </span>
              </span>
              <svg
                className="cp-chl-dash-card-chevron"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default ChallengeProjectDashboard;
